import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const MUSIC_GENRES = [
  // Popular/Modern
  'pop',
  'rock',
  'hip-hop',
  'rap',
  'electronic',
  'indie',
  'alternative',
  'r&b',
  'soul',
  'funk',
  'disco',
  'dance',
  'edm',

  // Rock subgenres
  'hard rock',
  'soft rock',
  'progressive rock',
  'psychedelic rock',
  'punk',
  'punk rock',
  'grunge',
  'post-punk',
  'garage rock',
  'glam rock',
  'art rock',

  // Metal
  'metal',
  'heavy metal',
  'thrash metal',
  'death metal',
  'black metal',
  'doom metal',
  'progressive metal',
  'metalcore',
  'nu metal',
  'power metal',

  // Electronic subgenres
  'house',
  'deep house',
  'tech house',
  'progressive house',
  'techno',
  'trance',
  'dubstep',
  'drum & bass',
  'jungle',
  'breakbeat',
  'downtempo',
  'trip-hop',
  'idm',
  'glitch',
  'electro',
  'synthpop',
  'vaporwave',
  'chillwave',
  'future bass',
  'trap',
  'hardstyle',
  'gabber',
  'ambient',
  'drone',
  'dark ambient',

  // Hip-Hop/Rap subgenres
  'boom bap',
  'lo-fi hip-hop',
  'conscious hip-hop',
  'gangsta rap',
  'mumble rap',
  'cloud rap',
  'drill',
  'grime',

  // Jazz
  'jazz',
  'bebop',
  'cool jazz',
  'hard bop',
  'free jazz',
  'fusion',
  'smooth jazz',
  'latin jazz',
  'jazz funk',
  'acid jazz',
  'nu jazz',

  // Classical/Orchestral
  'classical',
  'baroque',
  'romantic',
  'contemporary classical',
  'minimalism',
  'orchestral',
  'chamber music',
  'opera',
  'symphonic',

  // Blues
  'blues',
  'delta blues',
  'chicago blues',
  'electric blues',
  'blues rock',

  // Country/Folk
  'country',
  'folk',
  'bluegrass',
  'americana',
  'alt-country',
  'outlaw country',
  'folk rock',
  'singer-songwriter',
  'acoustic',

  // Latin
  'latin',
  'salsa',
  'bachata',
  'merengue',
  'reggaeton',
  'cumbia',
  'bossa nova',
  'samba',
  'tango',

  // Reggae/Caribbean
  'reggae',
  'dancehall',
  'dub',
  'ska',
  'rocksteady',
  'calypso',
  'soca',

  // World Music
  'world',
  'afrobeat',
  'afro-pop',
  'highlife',
  'kwaito',
  'arabic',
  'indian classical',
  'bollywood',
  'celtic',
  'klezmer',
  'flamenco',

  // Asian Pop
  'k-pop',
  'j-pop',
  'c-pop',
  'mandopop',
  'cantopop',

  // Experimental/Avant-garde
  'experimental',
  'noise',
  'avant-garde',
  'musique concrète',
  'electroacoustic',

  // Vocal/Choral
  'a cappella',
  'barbershop',
  'gospel',
  'choral',
  'sacred',

  // Instrumental
  'instrumental',
  'piano',
  'guitar',
  'violin',
  'cello',
  'flute',
  'saxophone',
  'trumpet',
  'drums',
  'percussion',
  'harp',
  'organ',
  'synthesizer',

  // Other
  'lo-fi',
  'emo',
  'screamo',
  'post-rock',
  'math rock',
  'shoegaze',
  'dream pop',
  'new wave',
  'synthwave',
  'darkwave',
  'gothic',
  'industrial',
  'noise rock',
  'hardcore',
  'post-hardcore',
  'emocore',
  'soundtrack',
  'score',
  'video game music',
  'anime music',
  'children\'s music',
  'comedy',
  'spoken word',
  'poetry',
  'meditation',
  'new age',
  'easy listening',
  'lounge',
  'exotica',
  'swing',
  'big band',
  'ragtime',
  'boogie-woogie',
  'doo-wop',
  'motown',
  'ai-generated',
];

interface GenreSelectorProps {
  selectedGenres: string[];
  onChange: (genres: string[]) => void;
  className?: string;
}

export function GenreSelector({ selectedGenres, onChange, className }: GenreSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredGenres = useMemo(() => {
    if (!search.trim()) return MUSIC_GENRES;

    const searchLower = search.toLowerCase();
    return MUSIC_GENRES.filter((genre) =>
      genre.toLowerCase().includes(searchLower)
    );
  }, [search]);

  const toggleGenre = (genre: string) => {
    if (selectedGenres.includes(genre)) {
      onChange(selectedGenres.filter((g) => g !== genre));
    } else {
      onChange([...selectedGenres, genre]);
    }
  };

  const removeGenre = (genre: string) => {
    onChange(selectedGenres.filter((g) => g !== genre));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedGenres.length > 0
              ? `${selectedGenres.length} genre${selectedGenres.length > 1 ? 's' : ''} selected`
              : 'Select genres...'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <div className="flex flex-col">
            {/* Search input */}
            <div className="p-3 border-b">
              <Input
                placeholder="Search genres..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9"
              />
            </div>

            {/* Genre list */}
            <div
              className="max-h-[300px] overflow-y-auto p-1"
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredGenres.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No genre found.
                </div>
              ) : (
                filteredGenres.map((genre) => (
                  <div
                    key={genre}
                    onClick={() => toggleGenre(genre)}
                    className={cn(
                      "flex items-center gap-2 px-2 py-2 cursor-pointer rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                      selectedGenres.includes(genre) && "bg-accent/50"
                    )}
                  >
                    <Checkbox
                      checked={selectedGenres.includes(genre)}
                      onCheckedChange={() => toggleGenre(genre)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="flex-1 capitalize text-sm">{genre}</span>
                    {selectedGenres.includes(genre) && (
                      <Check className="h-4 w-4" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Selected genres badges */}
      {selectedGenres.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {selectedGenres.map((genre) => (
            <Badge key={genre} variant="secondary" className="gap-1 capitalize">
              {genre}
              <button
                type="button"
                onClick={() => removeGenre(genre)}
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {selectedGenres.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="h-6 text-xs"
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
